import { useEffect, useRef, useState } from 'react';

/**
 * Hook para gerenciar embed do Twitter
 * Segue Atomic Design - lógica extraída do ArticleModal
 */
const useTwitterEmbed = (article, isOpen) => {
  const embedRef = useRef(null);
  const embedCreated = useRef(false);
  const lastRenderedTweetId = useRef(null);
  const [loading, setLoading] = useState(false);

  // Extrair ID do tweet da URL
  const getTweetId = (url) => {
    if (!url) return null;
    const match = url.match(/status\/(\d+)/);
    return match ? match[1] : null;
  };

  // Carregar script do Twitter uma vez
  useEffect(() => {
    if (window.twttr) return;

    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.charset = 'utf-8';
    document.body.appendChild(script);
  }, []);

  const isTwitter = article?.mediaType === 'Twitter' ||
                    article?.siteName === 'X (Twitter)' ||
                    (article?.videoUrl && article.videoUrl.includes('x.com')) ||
                    (article?.siteUrl && article.siteUrl.includes('x.com'));

  const tweetId = getTweetId(article?.videoUrl || article?.siteUrl);

  // Criar embed do Twitter apenas quando tweetId mudar
  useEffect(() => {
    if (!isOpen || !article) return;
    if (!isTwitter) return;
    if (!tweetId) return;

    const shouldRecreate = lastRenderedTweetId.current !== tweetId || !embedCreated.current;
    if (!shouldRecreate) return;

    embedCreated.current = false;
    setLoading(true);
    if (embedRef.current) embedRef.current.innerHTML = '';

    let isMounted = true;

    const waitForRef = () => {
      if (!isMounted) return;

      if (!embedRef.current) {
        setTimeout(waitForRef, 100);
        return;
      }

      const maxAttempts = 20;
      let attempts = 0;

      const createEmbed = () => {
        if (!isMounted) return;

        attempts++;

        if (window.twttr && window.twttr.widgets && embedRef.current) {
          if (!embedCreated.current) {
            embedRef.current.innerHTML = '';

            window.twttr.widgets.createTweet(
              tweetId,
              embedRef.current,
              {
                theme: 'dark',
                align: 'center',
                conversation: 'none',
                dnt: true
              }
            ).then(() => {
              if (isMounted) {
                embedCreated.current = true;
                lastRenderedTweetId.current = tweetId;
                setLoading(false);
              }
            }).catch(() => {
              if (isMounted) {
                setLoading(false);
              }
            });
          }
        } else if (attempts < maxAttempts) {
          setTimeout(createEmbed, 300);
        } else {
          setLoading(false);
        }
      };

      createEmbed();
    };

    setTimeout(waitForRef, 100);

    return () => {
      isMounted = false;
    };
  }, [isOpen, isTwitter, tweetId]);

  return {
    embedRef,
    loading,
    getTweetId
  };
};

export default useTwitterEmbed;
